import { DocumentService } from '@onviobr/toolbox-document-services/dist/esm5';
import { WebViewerActionSet } from '@onviobr/toolbox-file-viewer';
import {
	ParentItem, CancellationTokenSource, RenamedDocument,
	ToolbarServicesFacade, CancellationToken
} from './file-preview-toolbar-services-facade';
import { getCurrentUser } from '@onviobr/services/profile/user';
import { OpenItemService } from '@onviobr/document-components';
import { IAuthenticatedSessionBindings, Options } from '@onviobr/services/session';
import { DocumentEvent, IDocument2, IParent, IParentItem, LegacyLock } from './file-preview.model';
import { ContainerKey, Document, FileDrawer, ItemType } from '@onviobr/toolbox-document-services/dist/esm5/models';
import { LegacyDocument } from './file-preview-toolbar-services-facade';
import { IAction, LockActionResult, UnlockActionResult } from '@onviobr/toolbox-file-viewer/dist/types/toolbar/action-interface';

export class FilePreviewToolbarActionFactory implements CancellationTokenSource {
	private cancellationSource: CancellationTokenSource;

	public constructor(
		private getBindings: ({ force, auth }?: Options) => Promise<IAuthenticatedSessionBindings>,
		private documentService: DocumentService,
		private openItemService: OpenItemService,
		private legacyServices: ToolbarServicesFacade,
		private getDocuments: () => Array<IDocument2>,
		private getDocument: () => IDocument2,
		private actionReporter: (id: string, action: DocumentEvent) => void,
		private drawer: FileDrawer
	) {
		this.cancellationSource = this
			.legacyServices
			.dmsCancellationTokenSourceFactory
			.createCancellationTokenSource();
	}

	public getToken(): CancellationToken {
		return this.cancellationSource.getToken();
	}

	public cancel(): void {
		this.cancellationSource.cancel();
	}

	public reset(): void {
		this.cancellationSource.reset();
	}

	public createToolbarActions(): WebViewerActionSet {
		const removeCircularRefsInFolderTree = function(item): void {
			if (!item) {
				return;
			}

			delete item.subContainers;
			removeCircularRefsInFolderTree(item.parentItem);
		};

		const getDocumentLock = async (document) => {
			const itemKey = {
				id: document.id,
				type: ItemType.Document,
			};
			const containerKey = new ContainerKey(
				document.parentItem.id,
				document.parentItem.containerType === 'Projects' ? ItemType.Project : ItemType.Folder,
			);
			const updatedDocument = await this.documentService.get(itemKey, containerKey);

			return (updatedDocument.legacyItem as IDocument2)?.lock;
		};

		const copy: IAction<string, false | [
			destination: ParentItem,
			notificationPromise: Promise<any>
		]> = {
			action: async (documentId: string): Promise<false | [
				destination: ParentItem,
				notificationPromise: Promise<any>
			]> => {
				const document = this.getDocuments().find(d => d.id === documentId);
				const legacyDocument = document.legacyItem;
				const startContainer = this.legacyServices.getContainer ? this.legacyServices.getContainer() : legacyDocument.parentItem;

				const modalResultContainer = this.legacyServices.dmsCopyFolderSelectionModalService.open(
					[legacyDocument],
					{ startContainer }
				);

				const modalPromise = modalResultContainer.result;
				let chosenDestination: ParentItem;

				try {
					chosenDestination = await modalPromise;
				} catch (error) {
					removeCircularRefsInFolderTree(legacyDocument.parentItem);
					// modal services use errors to express cancellation;
					// we use 'false'
					if (error === 'cancel') {
						return false;
					}

					// indeed, error
					throw error;
				}

				removeCircularRefsInFolderTree(legacyDocument.parentItem);
				const completedNotification = this.legacyServices.$q.defer();
				completedNotification.promise.catch(() => {
					/* file name collision alert uses error
					to notify and ask for keep both/replace choice,
					so do nothing here */
				});

				const copyTask = this.legacyServices.fileManager
					.copyDocument(
						legacyDocument,
						chosenDestination.id,
						chosenDestination.itemType,
						completedNotification
					).promise as Promise<any>;

				try {
					await copyTask;
				} catch (error) {
					if (error?.httpStatusCode === 409) {
						// file name collision, so no actually error
						return [chosenDestination, completedNotification.promise];
					}

					throw error;
				}

				return [chosenDestination, completedNotification.promise];
			},
			onSuccess: async (copyResult: false | [
				destination: ParentItem,
				notificationPromise: Promise<any>
			]): Promise<void> => {
				if (copyResult === false) {
					return;
				}

				const [destination, completedNotification] = copyResult;
				const key = {
					type: this.legacyServices.dmsModels.toItemType(destination.itemType),
					id: destination.id
				};

				await completedNotification;

				// ready to refresh destination folder now
				this.legacyServices.dmsTreeStore.updateChildren(key);

				return Promise.resolve();
			},
			onError: (error: any): Promise<any> => {
				console.error('copy operation error', error);

				return Promise.resolve();
			}
		};

		const move: IAction<string, false | [
			destination: IParentItem & IParent,
			notificationPromise: Promise<any>
		]> = {
			action: async (documentId: string) => {
				const document = this.getDocuments().find(d => d.id === documentId);
				const legacyDocument = (document as any).legacyItem;
				const startContainer = this.legacyServices.getContainer ? this.legacyServices.getContainer() : legacyDocument.parentItem;

				const modalResultContainer = this.legacyServices.dmsMoveFolderSelectionModalService.open(
					[legacyDocument],
					{ startContainer }
				);

				const modalPromise = modalResultContainer.result as any as Promise<any>;
				let chosenDestination: any;

				try {
					chosenDestination = await modalPromise;
				} catch (error) {
					removeCircularRefsInFolderTree(legacyDocument.parentItem);
					// modal services use errors to express cancellation;
					// we use 'false'
					if (error === 'cancel') {
						return false;
					} else {
						// indeed, error
						throw error;
					}
				}

				removeCircularRefsInFolderTree(legacyDocument.parentItem);
				const completedNotification = this.legacyServices.$q.defer();
				completedNotification.promise.catch(() => {
					/* file name collision alert uses error
					to notify and ask for keep both/replace choice,
					so do nothing here */
				});

				const moveTask = this.legacyServices.fileManager
					.moveDocument(legacyDocument, chosenDestination, completedNotification)
					.promise as Promise<any>;

				try {
					await moveTask;
				} catch (error) {
					if (error?.httpStatusCode === 409) {
						// file name collision, so no actually error
						return [chosenDestination, completedNotification.promise];
					}

					throw error;
				}

				return [chosenDestination, completedNotification.promise];
			},
			onSuccess: async (moveResult: false | [
				destination: IParentItem & IParent,
				notificationPromise: Promise<any>
			]): Promise<void> => {
				if (moveResult === false) {
					return;
				}

				const [destination, completedNotification] = moveResult;
				const key = {
					type: this.legacyServices.dmsModels.toItemType(destination.itemType),
					id: destination.id
				};

				await completedNotification;

				// ready to refresh destination folder now
				this.legacyServices.dmsTreeStore.updateChildren(key);

				this.actionReporter(moveResult[0].id, DocumentEvent.Moved);

				return Promise.resolve();
			},
			onError: (error) => {
				console.error('move operation error', error);

				return Promise.resolve();
			}
		};

		const open: IAction<string, LockActionResult<LegacyLock>> = {
			action: async (documentId: string): Promise<LockActionResult<LegacyLock>> => {
				const session = await this.getBindings();
				const user = await getCurrentUser();
				const currentDocument = this.getDocument();
				const document = this.getDocuments().find(d => d.id === documentId);
				const legacyDocument = (document as any).legacyItem;

				if (!!currentDocument) {
					const contactId = session.ApplicationConfiguration.ContactId;

					const url = this.documentService.getOnvioLinkUrl(
						currentDocument as any as Document,
						user.blueID,
						contactId
					);

					const poller = this.legacyServices.dmsPollerFactory.createPoller(
						() => getDocumentLock(legacyDocument)
					);

					const cancellationToken = this.cancellationSource.getToken();

					// by default, the library for processing custom protocol waits for 2 seconds.
					// Apparently, onvio link often needs more time, 5 seconds seems to be a good heuristic
					this.openItemService.customProtocolCheck(url, undefined, undefined, 5000);
					const attempts = 15;
					const intervalMilliseconds = 1000;

					return poller
						.poll(attempts, intervalMilliseconds, cancellationToken)
						.then((lock: LegacyLock | undefined) => {
							if (typeof lock !== 'undefined') {
								return {
									documentId: legacyDocument.id,
									lockResult: lock,
								};
							} else {
								return {
									documentId: legacyDocument.id,
									lockResult: false,
								};
							}
						});
				}
			},
			onSuccess: async (result: LockActionResult<LegacyLock>) => {
				if (result.lockResult !== false) {
					const document = this.getDocuments().find(d => d.id === result.documentId);

					if (document) {
						const legacyDocument = (document as any).legacyItem;
						document.lock = legacyDocument.lock = result.lockResult;

						if (!!this.legacyServices.eventTrigger) {
							this.legacyServices.eventTrigger('dms-grid-refresh');
							this.legacyServices.eventTrigger('dms-opened-locally');
						}
					}
				}
			}
		};

		const approve: IAction<string, any> =
			this.legacyServices.dmsApprovalModalService &&
			(this.drawer === 'Client') ? {
					action: (documentId: string) => {
						const document = this.getDocuments().find(d => d.id === documentId);
						const legacyDocument = (document as any).legacyItem;
						const clientId_testUnsafe = legacyDocument.customFields.clientId;

						return this.legacyServices.dmsApprovalModalService.open({
							documents: [legacyDocument],
							client: {
								id: clientId_testUnsafe,
								itemType: 'Client',
							}
						});
					},
					onSuccess: async (result: any): Promise<void> => {
						if (result) {
							result.documents.map(async (document) => {
								const newLock = await getDocumentLock(document);
								const legacyDocument = this.getDocuments().find(x => x.id === document.id);
								legacyDocument.legacyItem.lock = legacyDocument.lock = newLock;
							});

							if (!!this.legacyServices.eventTrigger) {
								this.legacyServices.eventTrigger('dms-grid-refresh');
							}
						}
					},
					onError: (error: any) => {
						console.error('Approve action failed to complete', error);

						return Promise.resolve();
					}
				} : null;

		const send: IAction<string, any> =
			this.legacyServices.dmsSendDocumentsModalService ? {
				action: (documentId: string) => {
					const document = this.getDocuments().find(d => d.id === documentId);
					const legacyDocument = (document as any).legacyItem;
					const clientId = legacyDocument.customFields?.clientId ?? legacyDocument.clientId;

					return this.legacyServices.dmsSendDocumentsModalService.open({
						documents: [legacyDocument],
						client: {
							id: clientId,
							itemType: 'Client',
						},
						rootDrawer: this.drawer,
					});
				},
				onError: (error: any) => {
					console.error('Send action failed to complete', error);

					return Promise.resolve();
				},
			} : null;

		const rename: IAction<string, [RenamedDocument, LegacyDocument]> = {
			action: async (documentId: string): Promise<[RenamedDocument, LegacyDocument]> => {
				const document = this.getDocuments().find(d => d.id === documentId);
				const legacyDocument = document.legacyItem;

				const renamePromise = this.legacyServices.dmsRenameFileService
					.open(legacyDocument)
					.result;

				const renamedDocument = await renamePromise;
				// we're going to need to know document's container for refreshing, so pass it
				// as a part of result
				return Promise.resolve([renamedDocument, legacyDocument]);
			},
			onSuccess: (renameResult): Promise<void> => {
				const renamedDocument = (renameResult as Array<any>)[0];
				const legacyDocument = (renameResult as Array<any>)[1];
				legacyDocument.name = renamedDocument.name;
				const container = legacyDocument.parentItem;

				const key = {
					type: this.legacyServices.dmsModels.toItemType(container.itemType),
					id: container.id
				};
				this.legacyServices.dmsTreeStore.updateChildren(key);

				this.actionReporter(legacyDocument.id, DocumentEvent.Renamed);

				return Promise.resolve();
			}
		};

		const insertScan: IAction<string, any> = {
			action: async () => {
				window.location.href = window.location.href + '/insert-scan';
			}
		};

		const deleteDocument: IAction<string, any> = {
			action: async (documentId: string) => {
				const document = this.getDocuments().find(d => d.id === documentId);
				const legacyDocument = document.legacyItem;

				const additionalProps = {
					containerType: legacyDocument.parentItem.containerType || legacyDocument.parentItem.itemType,
					containerId: legacyDocument.parentItem.id,
				};

				const arg = {
					busily: false,
					params: {
						trashRequest: Object.assign(legacyDocument, additionalProps),
					},
				};

				const deleteTask = this.legacyServices.dmsRecycleBinUiService.moveDocumentToTrash(arg).promise;

				this.actionReporter(documentId, DocumentEvent.Deleted);

				return deleteTask;
			},
		};

		const unlock: IAction<string, UnlockActionResult> = {
			action: async (documentId: string): Promise<UnlockActionResult> => {
				const document = this.getDocuments().find(d => d.id === documentId);
				const legacyDocument = (document as any).legacyItem;

				const unlockResult = this.legacyServices.dmsUnlockDocumentService.unlock(legacyDocument);

				return unlockResult;
			},
			onSuccess: (result: UnlockActionResult): Promise<void> => {
				if (result.successful) {
					const docIds = (result.detail as {
						documentsToUnlock?: Array<string>;
						unlockResult: any;
					})?.documentsToUnlock;

					if (docIds) {
						const documents = this.getDocuments();
						docIds.forEach(id => {
							const document = documents.find(d => d.id === id);

							if (document) {
								delete document.lock;
								delete document.legacyItem.lock;
							}
						});
					}

					this.legacyServices.dmsUnlockDocumentService.notifyUnlockDone();
				}

				return Promise.resolve();
			}
		};

		const actions: WebViewerActionSet = {
			Open: open,
			Approve: approve,
			Send: send,
			CopyDocument: copy,
			MoveDocument: move,
			RenameDocument: rename,
			InsertScan: insertScan,
			Unlock: unlock,
			DeleteDocument: deleteDocument,
			Download: {
				// Although download action is completely file-viewer's job,
				// we still want to control it.
				//  - skipping Download action here would end up
				// file-viewer not showing such item in a toolbar.
				//  - returning resolved Promise would mean successful completion
				// of an 'action', file-viewer then can proceed with its onSuccess handler
				// and actually download the document
				//  - finally, we could return rejected promise, which would mean failed action
				// and thus no onSuccess run
				action: (_documentId: string) => Promise.resolve()
			}
		};

		return actions;
	}
}
